<template>
  <div class="">
    <div class="grid grid-cols-6 gap-4">
      <input v-for="(digit, index) in digits" :key="index" :ref="`otpInput${index}`" v-model="digits[index]" type="text"
        maxlength="1" class="w-8 py-2 border-gray-200 text-center text-xl font-bold"
        :class="{ 'has-value': digits[index] }" @input="handleInput(index, $event)"
        @keydown="handleKeydown(index, $event)" @paste="handlePaste($event)" autocomplete="off" inputmode="numeric"
        pattern="[0-9]*" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'OtpInput',
  props: {
    value: {
      type: String,
      default: ''
    },
    length: {
      type: Number,
      default: 6
    }
  },
  data() {
    return {
      digits: Array(this.length).fill('')
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.digits = newValue.split('').slice(0, this.length)
          // Rellenar con strings vacíos si es necesario
          while (this.digits.length < this.length) {
            this.digits.push('')
          }
        } else {
          this.digits = Array(this.length).fill('')
        }
      }
    },
    digits: {
      deep: true,
      handler() {
        const otpValue = this.digits.join('')
        this.$emit('input', otpValue)

        // Emitir evento cuando se complete el OTP
        if (otpValue.length === this.length && otpValue.replace(/\s/g, '').length === this.length) {
          this.$emit('complete', otpValue)
        }
      }
    }
  },
  methods: {
    handleInput(index, event) {
      const value = event.target.value

      // Solo permitir números
      if (!/^\d*$/.test(value)) {
        event.target.value = this.digits[index]
        return
      }

      // Si se ingresa un valor, actualizar y mover al siguiente
      if (value) {
        this.digits[index] = value
        this.moveToNext(index)
      }
    },

    handleKeydown(index, event) {
      // Manejar backspace
      if (event.key === 'Backspace') {
        if (this.digits[index]) {
          this.digits[index] = ''
        } else if (index > 0) {
          this.moveToPrevious(index)
        }
      }
      // Manejar flechas de navegación
      else if (event.key === 'ArrowRight' && index < this.length - 1) {
        this.moveToNext(index)
      }
      else if (event.key === 'ArrowLeft' && index > 0) {
        this.moveToPrevious(index)
      }
      // Manejar Enter
      else if (event.key === 'Enter') {
        const otpValue = this.digits.join('')
        if (otpValue.length === this.length) {
          this.$emit('submit', otpValue)
        }
      }
    },

    handlePaste(event) {
      event.preventDefault()
      const pastedData = event.clipboardData.getData('text/plain')
      const cleanedData = pastedData.replace(/\D/g, '').slice(0, this.length)

      if (cleanedData) {
        const newDigits = cleanedData.split('')
        // Rellenar el resto con strings vacíos
        while (newDigits.length < this.length) {
          newDigits.push('')
        }
        this.digits = newDigits

        // Enfocar el siguiente input vacío o el último
        const nextEmptyIndex = this.digits.findIndex(digit => !digit)
        const targetIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : this.length - 1
        this.$nextTick(() => {
          this.focusInput(targetIndex)
        })
      }
    },

    moveToNext(currentIndex) {
      if (currentIndex < this.length - 1) {
        this.$nextTick(() => {
          this.focusInput(currentIndex + 1)
        })
      }
    },

    moveToPrevious(currentIndex) {
      if (currentIndex > 0) {
        this.$nextTick(() => {
          this.focusInput(currentIndex - 1)
        })
      }
    },

    focusInput(index) {
      const input = this.$refs[`otpInput${index}`]
      if (input && input[0]) {
        input[0].focus()
      }
    },

    // Método público para limpiar el OTP
    clear() {
      this.digits = Array(this.length).fill('')
      this.$nextTick(() => {
        this.focusInput(0)
      })
    },

    // Método público para enfocar el primer input
    focus() {
      this.$nextTick(() => {
        this.focusInput(0)
      })
    }
  }
}
</script>

<style scoped>
input:active,
input:focus {
  border-color: #25DD21;
  outline: none;
}

.border-gray-200 {
  border-color: #ADADAD;
  border-width: 1px;
  border-style: solid;
  border-radius: 8px;
}

.border-gray-200:disabled {
  background-color: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
}
</style>
